import { useContext, useEffect, useRef, useState } from 'react';
import { useRegisterEvents, useSigma, useSetSettings } from '@react-sigma/core';
import { useAppDispatch, useAppSelector } from '../../lib/hooks';
import { addFloatingDoc, defaultFilterOptions, incrementRefreshCounter } from '../../lib/state/slices/ProjectStateSlice';
import { GraphViewContext } from './Index';
import { FilterGraphNodes } from '@/lib/SearchFilter';

const GraphEvents = (props: {
  onHoverNode: Function;
  isActive?: boolean;
  dragAndDrop?: boolean;
  setNodeContextMenu: any;
}) => {
  const registerEvents = useRegisterEvents();
  const sigma = useSigma();
  const setSettings = useSetSettings();
  const dispatch = useAppDispatch();
  const graph = useSigma().getGraph();
  const graphView = useContext(GraphViewContext);

  const documentSearchType = useAppSelector((state) => state.viewReducer.documents.search.type);
  const documentSearchQuery = useAppSelector((state) => state.viewReducer.documents.search.query[documentSearchType]);
  const filter = useAppSelector((state) => state.viewReducer.documents.filter);
  const refreshCounter = useAppSelector((state) => state.viewReducer.refreshCounter);

  if (graphView.filter === undefined) {
    graphView.filter = defaultFilterOptions
  }
  
  const graphFilterHUDOC = graphView.filter.source.hudoc;

  const [clickedNodes, setClickedNodes] = useState<Set<string>>(new Set());
  // const degreeFilter = useAppSelector((state) => state.viewReducer.graphViews[props])

  const mouseDownedNodeRef = useRef<string | null>(null);
  const movedUnderClickRef = useRef(false); // This is used to see if the user tries to drag and drop or open a node

  useEffect(() => {
    if (!sigma) return;
    setTimeout(() => {
      try {
        if (!sigma) return;
        sigma.resize();
        sigma.refresh();
      } catch (error) {
        // console.error('Error refreshing graph:', error);
        dispatch(incrementRefreshCounter());
      }
    }, 300);
  }, [refreshCounter, sigma, dispatch]);

  try {
    sigma.getGraph().getNodeAttributes(mouseDownedNodeRef);
  } catch (e) {}

  // Filter implementation
  useEffect(() => {

    // console.log(documentSearchQuery, graph.nodes(), graphView.centerNode, filter);
    
    const filteredNodes = FilterGraphNodes(
      documentSearchQuery,
      graph,
      graphView.centerNode,
      filter,
    );
    
    // console.log(filteredNodes);
    
    const filteredNodeIds = filteredNodes.map((e) => e.elementId);
    
    graph.forEachNode((nodeID) => {
      
      if (graph.getNodeAttribute(nodeID, 'hidden') !== undefined) {
        // Hide node if it is NOT found in the filteredNodeIds
        graph.setNodeAttribute(nodeID, 'hidden', !filteredNodeIds.includes(nodeID));
      }
    });
  }, [
    filter,
    documentSearchQuery,
    graphFilterHUDOC.articles,
    graphFilterHUDOC.conclusion,
    graphFilterHUDOC.countries,
    graphFilterHUDOC.decisionLevel,
    graphFilterHUDOC.importance,
    graphView.centerNode,
    graph,
    sigma,
  ]);

  function clickNodeOpenDoc() {
    let nodeId = mouseDownedNodeRef.current;
    if (!nodeId) return;
    props.setNodeContextMenu(undefined);

    let newClickedNodes = new Set(clickedNodes);

    if (newClickedNodes.has(nodeId)) {
      newClickedNodes.delete(nodeId);
      graph.forEachEdge((edge) => {
        graph.setEdgeAttribute(edge, 'hidden', false);
      });
    } else {
      newClickedNodes.add(nodeId);
      graph.forEachEdge((edge) => {
        if (!graph.extremities(edge).includes(nodeId)) {
          graph.setEdgeAttribute(edge, 'hidden', true);
        } else {
          graph.setEdgeAttribute(edge, 'hidden', false);
        }
      });
    }
    setClickedNodes(newClickedNodes);

    let node_attributes = graph.getNodeAttributes(nodeId);
    dispatch(
      addFloatingDoc({
        graphViewId: graphView.id,
        nodeId,
        id: node_attributes.id,
      }),
    );
    props.onHoverNode(undefined);
    props.setNodeContextMenu(undefined);
  }

  useEffect(() => {
    // Register the events
    registerEvents({
      updated: (event) => {
        // This should be empty at all times
      },

      enterNode: (event) => {
        if (mouseDownedNodeRef.current) return; // If the user tries to drag and drop, dont change the hover stuff
        setSettings({
          renderEdgeLabels: true,
        });
        props.onHoverNode({
          id: graph.getNodeAttributes(event.node).id,
          nodeId: event.node,
        });
        let neighbours = graph.neighbors(event.node);

        graph.forEachNode((node) => {
          if (!neighbours.includes(node)) {
            graph.setNodeAttribute(node, 'color', '#E2E2E2');
            graph.setNodeAttribute(node, 'borderColor', '#E2E2E2');
          } else {
            graph.setNodeAttribute(node, 'color', graph.getNodeAttribute(node, 'originalColor'));
            graph.setNodeAttribute(
              node,
              'borderColor',
              graph.getNodeAttribute(node, 'originalBorderColor'),
            );
          }
        });
        graph.setNodeAttribute(
          event.node,
          'color',
          graph.getNodeAttribute(event.node, 'originalColor'),
        );
        graph.setNodeAttribute(
          event.node,
          'borderColor',
          graph.getNodeAttribute(event.node, 'originalBorderColor'),
        );
        graph.forEachEdge((edge) => {
          if (!graph.extremities(edge).includes(event.node)) {
            graph.setEdgeAttribute(edge, 'hidden', true);
          } else {
            graph.setEdgeAttribute(edge, 'hidden', false);
          }
        });
      },

      leaveNode: (payload) => {
        setSettings({
          renderEdgeLabels: false,
        });
        props.onHoverNode(undefined);
        let centerNode = graphView.centerNode;

        graph.forEachNode((node) => {
          graph.setNodeAttribute(node, 'color', graph.getNodeAttribute(node, 'originalColor'));
          graph.setNodeAttribute(
            node,
            'borderColor',
            graph.getNodeAttribute(node, 'originalBorderColor'),
          );
        });
        if (clickedNodes.size > 0) {
          graph.forEachEdge((edge) => {
            let isConnectedToClickedNode = Array.from(clickedNodes).some((clickedNodeId) =>
              graph.extremities(edge).includes(clickedNodeId),
            );
            graph.setEdgeAttribute(edge, 'hidden', !isConnectedToClickedNode);
          });
        } else {
          graph.forEachEdge((edge) => {
            [...graph.extremities(edge)]
              .map((node) => graph.getNodeAttribute(node, 'id') === centerNode)
              .includes(true)
              ? graph.setEdgeAttribute(edge, 'hidden', false)
              : graph.setEdgeAttribute(edge, 'hidden', true);
          });
        }
      },
      downNode: (e) => {
        if (e.event.original.button === 2) return; // This was a rightclick
        props.onHoverNode(undefined);
        movedUnderClickRef.current = false;
        mouseDownedNodeRef.current = e.node;
        if (props.dragAndDrop) {
          sigma.getGraph().setNodeAttribute(e.node, 'highlighted', true);
        }
      },
      mousemovebody: (e) => {
        if (!mouseDownedNodeRef.current) return;
        // Get new position of node
        movedUnderClickRef.current = true;
        if (props.dragAndDrop) {
          const pos = sigma.viewportToGraph(e);
          sigma.getGraph().setNodeAttribute(mouseDownedNodeRef.current, 'x', pos.x);
          sigma.getGraph().setNodeAttribute(mouseDownedNodeRef.current, 'y', pos.y);

          // Prevent sigma to move camera:
          e.preventSigmaDefault();
          e.original.preventDefault();
          e.original.stopPropagation();
        }
      },
      mouseup: (e) => {
        if (props.dragAndDrop) {
          if (mouseDownedNodeRef.current) {
            sigma.getGraph().removeNodeAttribute(mouseDownedNodeRef.current, 'highlighted');
          }
        }
        if (!movedUnderClickRef.current) {
          clickNodeOpenDoc();
        }
        mouseDownedNodeRef.current = null;
      },
      mousedown: (e) => {
        // Disable the autoscale at the first down interaction
        if (!sigma.getCustomBBox()) sigma.setCustomBBox(sigma.getBBox());
        if (props.dragAndDrop) {
          if (!sigma.getCustomBBox()) sigma.setCustomBBox(sigma.getBBox());
        }
        props.setNodeContextMenu(undefined);
      },
      mousemove: (e) => {},
      clickStage: (e) => {
        props.setNodeContextMenu(undefined);
      },

      rightClickNode: (event) => {
        event.event.original.preventDefault();
        props.setNodeContextMenu({
          id: graph.getNodeAttributes(event.node).id,
          nodeId: event.node,
        });
      },
    });
  }, [registerEvents, sigma, dispatch, graph, props]);

  return null;
};

export default GraphEvents;
